import  { useMemo, useState } from 'react';
import { Trash2, Minus, Plus, X } from 'lucide-react';
import { productos } from '../data/db/Products';
import toast from 'react-hot-toast';
import MobileBackToProductsButton from '../components/BackToProduct';
import useCartStore from '../context/CartStore'; // Importa el store de Zustand


const CartPage = () => {
  // Obtén el estado y las acciones del store
  const { items: cartItems, removeItem, updateItemQuantity, clearCart } = useCartStore();
  const [showModal, setShowModal] = useState(false);

  const handleRemoveItem = (id: number) => {
    removeItem(id);
    toast.success('Producto eliminado del carrito.');
  };

  const handleDecreaseQuantity = (id: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (item && item.cantidad > 1) {
      updateItemQuantity(id, item.cantidad - 1);
      toast.success('Cantidad disminuida.');
    }
  };

  const handleIncreaseQuantity = (id: number) => {
    const producto = productos.find((p) => p.id === id);
    const item = cartItems.find((item) => item.id === id);

    if (producto && item) {
      if (item.cantidad < producto.stock) {
        updateItemQuantity(id, item.cantidad + 1);
        toast.success('Cantidad aumentada.');
      } else {
        toast.error('No hay stock suficiente para este producto.');
      }
    }
  };

  const { total } = useMemo(() => {
    const subtotal = cartItems.reduce((acc, item) => acc + item.precio * item.cantidad, 0);
    return { subtotal, total: subtotal };
  }, [cartItems]);

  const handleProcessPurchase = () => {
    if (cartItems.length === 0) {
      toast.error('El carrito está vacío.');
      return;
    }
    setShowModal(true);
  };

  const handleConfirmPurchase = () => {
    sendToWhatsApp();
    clearCart();
    setShowModal(false);
  };

  const sendToWhatsApp = () => {
    const phoneNumber = "18093041588"; // Reemplaza con tu número de WhatsApp
    const orderDetails = cartItems
      .map((item) => `🔹 *${item.titulo}* (${item.categoria}) - Cantidad: ${item.cantidad} - Precio: $${item.precio.toFixed(2)}`)
      .join("\n");

    const message = `Hola, me gustaría comprar estos productos:\n\n${orderDetails}\n\n💰 *Total: $${total.toFixed(2)}*`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="min-h-screen mt-16 md:mt-20 bg-main font-poppins p-6">
      <MobileBackToProductsButton />
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-6">
        <div className="bg-main shadow-lg rounded-lg overflow-hidden flex-1">
          <div className="p-6">
            <h1 className="text-2xl md:text-3xl font-bold text-primary-dark text-center mb-8">Carrito de Compras</h1>
            <div className="hidden md:grid grid-cols-7 gap-4 text-primary-dark font-semibold border-b pb-4">
              <div>ID</div>
              <div className="col-span-2">Producto</div>
              <div>Categoría</div>
              <div>Precio</div>
              <div>Cantidad</div>
              <div>Acciones</div>
            </div>
            {cartItems.map((item) => (
              <div key={item.id} className="grid grid-cols-2 md:grid-cols-7 gap-4 items-center py-4 border-b last:border-b-0">
                <div className="text-sm text-primary-dark font-medium">{item.id}</div>
                <div className="col-span-2 text-sm text-primary-dark">{item.titulo}</div>
                <div className="text-sm text-primary-dark">{item.categoria}</div>
                <div className="text-sm text-primary-dark">${item.precio.toFixed(2)}</div>
                <div className="text-sm text-primary-dark flex items-center gap-2">
                  <button type='button' title='disminuir cantidad' onClick={() => handleDecreaseQuantity(item.id)} className="p-1 rounded-full hover:bg-secondary-section">
                    <Minus size={16} />
                  </button>
                  <span>{item.cantidad}</span>
                  <button type='button' title='aumentar cantidad' onClick={() => handleIncreaseQuantity(item.id)} className="p-1 rounded-full hover:bg-secondary-section">
                    <Plus size={16} />
                  </button>
                </div>
                <button type='button' title='eliminar producto' onClick={() => handleRemoveItem(item.id)} className="p-1 rounded-full hover:bg-red-100">
                  <Trash2 size={16} className="text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full md:w-1/3 bg-secondary-section h-[13rem] p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold text-primary-dark text-center mb-6">Resumen de Compra</h2>
          <div className="space-y-4">
            <div className="flex justify-between">
              <span className="text-lg font-semibold text-dark">Total:</span>
              <span className="text-lg font-semibold text-dark">${total.toFixed(2)}</span>
            </div>
            <button type="button" onClick={handleProcessPurchase} className="w-full bg-primary text-white py-3 rounded-lg">
              Proceder al Pago
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 px-4 bg-black bg-opacity-50 overflow-y-auto flex justify-center items-center">
          <div className="bg-white p-6 rounded-lg shadow-lg overflow-auto  max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-dark">Resumen de Compra</h2>
              <button title='abrir modal' type='button' onClick={() => setShowModal(false)} className="p-2 rounded-full hover:bg-gray-100">
                <X size={20}  className='text-dark'/>
              </button>
            </div>
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={item.id} className="flex justify-between">
                  <span className='text-dark'>{item.titulo} (x{item.cantidad})</span>
                  <span className='text-dark'>${(item.precio * item.cantidad).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-4">
                <div className="flex justify-between text-dark font-semibold">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type='button' onClick={() => setShowModal(false)} className="px-4 py-2 text-dark bg-gray-300 rounded-lg">
                Cancelar
              </button>
              <button type='button' onClick={handleConfirmPurchase} className="px-4 py-2 bg-dark text-white rounded-lg">
                Ordenar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;